import * as React from "react";
import {useCallback, useState} from "react";
import GridElement from "../../Common/GridElement";
import {getVisibleColumns, prepareFilterQuery, prepareSortQuery} from "../../Utils/gridUtils";
import {Add, Delete, Edit} from "@mui/icons-material";
import Button from "@mui/material/Button";
import ManageBookkeeperModal from "./ManageBookkeeperModal";
import {IconButton, Tooltip} from "@mui/material";
import Swal from "sweetalert2";
import {bulkImportBookkeepers, deleteBookkeeper, getBookkeepers} from "../../../API/bookkeeper";
import {toast} from "react-toastify";
import {getUserSettings} from "../../Utils/constants";
import {updateUserSetting} from "../../../API/userSettings";
import {fetchGet} from "../../../API/util";
import ImportModal from "../../Common/Modals/ImportModal";
import {useNavigate} from "react-router";

const isDateColumn = (key) => {
    return (key === "created_at");
}
const BookkeeperGrid = () => {
    const {user, userMeta} = getUserSettings();
    const lastSortBy = user?.tableSettings?.bookkeepers?.sortBy ? user.tableSettings.bookkeepers.sortBy : null;
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [rowsCount, setRowsCount] = useState(0);
    const [filters, setFilters] = useState(() => {
        return user?.tableSettings?.bookkeepers?.filters
        && Array.isArray(user.tableSettings.bookkeepers.filters) ? user.tableSettings.bookkeepers.filters : [];
    });
    const [controlledPageIndex, setControlledPageIndex] = useState(0);
    const [refreshGridData, setRefreshGridData] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [openManageBookkeeperModal, setOpenManageBookkeeperModal] = useState(false);
    const [bookkeeper, setBookkeeper] = useState(null);
    const [openImportBookkeeperModal, setOpenImportBookkeeperModal] = useState(false);
    const [cols, setCols] = useState(() => {
            let columns = userMeta.table_columns.bookkeepers;
            let visibleColumns = user.tableSettings.bookkeepers?.columns ? user.tableSettings.bookkeepers?.columns : [];
            let invisibleColumns = columns.filter((column) => !visibleColumns.includes(column));
            columns = [...visibleColumns, ...invisibleColumns];

            const dynamicColumns = getVisibleColumns(columns, visibleColumns, isDateColumn);
            dynamicColumns?.push({
                Header: "Action",
                filter: "fuzzyText",
                Cell: ({row}) => (
                    <div>
                        <Tooltip title='Edit' arrow placement='top'>
                            <IconButton
                                aria-label='edit bookkeeper'
                                aria-controls='menu-appbar'
                                aria-haspopup='true'
                                onClick={() => {
                                    setOpenManageBookkeeperModal(true);
                                    setBookkeeper(row.original);
                                }}
                                style={{
                                    color: "#000",
                                    fontSize: "17px",
                                    fontWeight: "600",
                                }}
                            >
                                <Edit color='primary'/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete Bookkeeper' arrow placement='top'>
                            <IconButton
                                aria-label='delete bookkeeper'
                                aria-controls='menu-appbar'
                                aria-haspopup='true'
                                onClick={() => handleDeleteBookkeeperClick(row.original)}
                                style={{
                                    color: "#FF0000",
                                    fontSize: "17px",
                                    fontWeight: "600",
                                }}
                                disabled={isDeleting}
                            ><Delete/></IconButton>
                        </Tooltip>
                    </div>
                ),
            });

            return dynamicColumns ? [...dynamicColumns] : [];
        }
    );

    const resetFilters = useCallback(() => setFilters([]), [setFilters]);
    const fetchData = useCallback(({pageSize, pageIndex, sortBy, filters, columns}) => {
        let _columns = columns.map((item) => item.column);
        _columns = _columns.filter((item) => item !== undefined);

        let query = prepareFilterQuery(filters, columns);
        let sort = prepareSortQuery(sortBy, columns);

        setLoading(true);

        getBookkeepers(pageSize, _columns, pageIndex, query, sort).then((res) => {
            setData(res.data.main);
            setPageCount(res.data.offset.highest_offset);
            setRowsCount(res.data.offset.rows_count);
            setLoading(false);

            const user = JSON.parse(localStorage.getItem("user"));
            updateUserSetting(user).then(() => {});
        }).catch((error) => {
            toast.error(error.error);
            setLoading(false);
        })

        setRefreshGridData(false); //if grid fetches latest data then set refreshGridData to false
    }, []);

    const handleManageBookkeeperClose = (refreshGrid = false) => {
        setOpenManageBookkeeperModal(false);
        setRefreshGridData(refreshGrid);
        setBookkeeper(null);
    };

    const handleDeleteBookkeeperClick = (data) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                setIsDeleting(true);
                deleteBookkeeper(data.bookkeeper_id).then((res) => {
                    setRefreshGridData(true);
                    setIsDeleting(false);
                }).catch(() => {
                    setIsDeleting(false);
                });
            }
        });
    }

    const downloadSample = async () => {
        try {
            const blob = await fetchGet('/bookkeepers/download/sample/csv', 0, {raw: true});

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'bookkeepers_sample.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (_error) {
            toast.error('Failed to download sample CSV.');
        }
    };

    const importBulkBookkeepers = async (file) => {
        await bulkImportBookkeepers(file);
    }

    return (
        <>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <h2 className='page-title'>Bookkeepers</h2>
                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{margin: '1px 2px'}}
                        onClick={downloadSample}>
                        Download Sample CSV
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{margin: '1px 2px'}}
                        onClick={() => {
                            setOpenImportBookkeeperModal(true);
                        }}>
                        Import Bookkeepers
                    </Button>
                    <Button style={{margin: '1px 2px'}} variant="contained" color="primary"
                            onClick={() => navigate('/bookkeepers-imports-history')}>
                        Bookkeepers Import History
                    </Button>
                    <Button
                        variant='contained'
                        color='primary'
                        style={{marginRight: '5px'}}
                        onClick={() => setOpenManageBookkeeperModal(true)}>
                        <Add aria-hidden='false' color='white'/>
                        Create Bookkeeper
                    </Button>
                </div>
            </div>

            <GridElement
                columns={cols}
                data={data}
                fetchData={fetchData}
                loading={loading}
                pageCount={pageCount}
                controlledPageIndex={controlledPageIndex}
                setControlledPageIndex={setControlledPageIndex}
                filters={filters}
                setFilters={setFilters}
                resetFilters={resetFilters}
                rowsCount={rowsCount}
                handleRowClick={() => {
                }}
                initialSortBy='created_at'
                sortOrder='desc'
                hideSelection={true}
                refreshGridData={refreshGridData}
                showColumnsControl={true}
                showCsvDownloadControl={true}
                setColumns={setCols}
                table="bookkeepers"
                lastSortBy={lastSortBy}
            />
            <ManageBookkeeperModal
                open={openManageBookkeeperModal}
                handleClose={handleManageBookkeeperClose}
                data={bookkeeper}/>
            <ImportModal
                open={openImportBookkeeperModal}
                title={'Import Bookkeepers'}
                handleClose={() => setOpenImportBookkeeperModal(false)}
                bulkImportRecords={importBulkBookkeepers}/>
        </>
    )
}

export default BookkeeperGrid;